import { useEffect, useRef, useState, type ReactNode } from "react";

/** Validated categorical palette (fixed order — colour follows the metric, never its rank). */
export const SERIES: Record<string, string> = {
  outreach: "#2340c4", follow_ups: "#e0702e", appointments: "#0094c8",
  client_moves: "#a2810f", recruit_moves: "#8a55c7", builder_moves: "#1c9a6e",
};
export const THIS_YEAR = "#132d9c";
export const LAST_YEAR = "#b9b8cc";

const nice = (max: number) => {
  if (max <= 0) return 4;
  const p = Math.pow(10, Math.floor(Math.log10(max)));
  const m = max / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10) * p;
};
const fmtN = (n: number) => (Math.abs(n) >= 1000 ? `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k` : String(Math.round(n * 10) / 10));

type Tip = { x: number; y: number; title: string; rows: { label: string; value: string; color?: string }[] } | null;

/** Measure the container so the SVG draws at real pixel size (text never scales). */
function useWidth(ref: React.RefObject<HTMLDivElement | null>, fallback = 640) {
  const [w, setW] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(240, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

function useTip() {
  const ref = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<Tip>(null);
  const show = (e: React.MouseEvent | React.FocusEvent, t: Omit<NonNullable<Tip>, "x" | "y">) => {
    const box = ref.current?.getBoundingClientRect();
    const tgt = (e.currentTarget as Element).getBoundingClientRect();
    if (!box) return;
    setTip({ ...t, x: tgt.left - box.left + tgt.width / 2, y: tgt.top - box.top });
  };
  const el = tip && (
    <div className="chart-tip" style={{ left: tip.x, top: tip.y }}>
      <b>{tip.title}</b>
      {tip.rows.map((r) => (
        <div key={r.label} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {r.color && <i style={{ width: 8, height: 8, borderRadius: 2, background: r.color, display: "inline-block" }} />}
          <span style={{ opacity: 0.8 }}>{r.label}</span><span style={{ marginLeft: "auto", fontWeight: 600 }}>{r.value}</span>
        </div>
      ))}
    </div>
  );
  return { ref, show, hide: () => setTip(null), el };
}

export function Legend({ items }: { items: { label: string; color: string; dashed?: boolean }[] }) {
  return (
    <div className="chart-legend">
      {items.map((i) => (
        <span key={i.label}>
          {i.dashed ? <i style={{ width: 14, height: 0, borderTop: `2px dashed ${i.color}`, borderRadius: 0 }} /> : <i style={{ background: i.color }} />}
          {i.label}
        </span>
      ))}
    </div>
  );
}

/**
 * Grouped columns over categories. series[].values aligned with labels.
 * Optional dashed target line (single value).
 */
export function Columns({ labels, series, target, height = 220, format = fmtN, tipTitle }: {
  labels: string[];
  series: { key: string; label: string; color: string; values: number[] }[];
  target?: { value: number; label: string };
  height?: number;
  format?: (n: number) => string;
  tipTitle?: (i: number) => string;
}) {
  const t = useTip();
  const W = useWidth(t.ref), H = height, L = 36, R = 8, T = 10, B = 26;
  const max = nice(Math.max(1, target?.value ?? 0, ...series.flatMap((s) => s.values)));
  const y = (v: number) => T + (H - T - B) * (1 - v / max);
  const band = (W - L - R) / Math.max(1, labels.length);
  const gap = 2, inner = Math.min(band * 0.72, 18 * series.length + gap * (series.length - 1));
  const bw = Math.max(2, (inner - gap * (series.length - 1)) / series.length);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  const showEvery = Math.max(1, Math.ceil(labels.length / Math.max(1, Math.floor((W - L - R) / 34))));
  return (
    <div className="chart" ref={t.ref}>
      {series.length > 1 || target ? <Legend items={[...series.map((s) => ({ label: s.label, color: s.color })), ...(target ? [{ label: target.label, color: "#3a3b55", dashed: true }] : [])]} /> : null}
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={series.map((s) => s.label).join(", ")}>
        <g className="grid">{ticks.map((v) => <line key={v} x1={L} x2={W - R} y1={y(v)} y2={y(v)} />)}</g>
        <g className="axis">
          {ticks.map((v) => <text key={v} x={L - 6} y={y(v) + 4} textAnchor="end">{format(v)}</text>)}
          {labels.map((l, i) => i % showEvery === 0 && <text key={i} x={L + band * i + band / 2} y={H - 8} textAnchor="middle">{l}</text>)}
        </g>
        {labels.map((l, i) => {
          const x0 = L + band * i + (band - inner) / 2;
          return (
            <g key={i}>
              {series.map((s, si) => {
                const v = s.values[i] ?? 0;
                const h = Math.max(0, y(0) - y(v));
                const x = x0 + si * (bw + gap);
                const r = Math.min(4, bw / 2, h);
                return h > 0 ? (
                  <path key={s.key} fill={s.color}
                    d={`M${x},${y(0)} V${y(v) + r} Q${x},${y(v)} ${x + r},${y(v)} H${x + bw - r} Q${x + bw},${y(v)} ${x + bw},${y(v) + r} V${y(0)} Z`} />
                ) : null;
              })}
              <rect x={L + band * i} y={T} width={band} height={H - T - B} fill="transparent" tabIndex={0}
                onMouseEnter={(e) => t.show(e, { title: tipTitle ? tipTitle(i) : l, rows: [...series.map((s) => ({ label: s.label, value: format(s.values[i] ?? 0), color: s.color })), ...(target ? [{ label: target.label, value: format(target.value) }] : [])] })}
                onFocus={(e) => t.show(e, { title: tipTitle ? tipTitle(i) : l, rows: series.map((s) => ({ label: s.label, value: format(s.values[i] ?? 0), color: s.color })) })}
                onMouseLeave={t.hide} onBlur={t.hide} />
            </g>
          );
        })}
        <line x1={L} x2={W - R} y1={y(0)} y2={y(0)} stroke="#d4d2e2" />
        {target && target.value > 0 && <line className="target" x1={L} x2={W - R} y1={y(target.value)} y2={y(target.value)} />}
      </svg>
      {t.el}
    </div>
  );
}

/** Horizontal bars: label · bar · value. Good for funnels/journeys and rankings. */
export function HBars({ rows, color = "var(--royal)", format = (n: number) => String(n) }: {
  rows: { label: ReactNode; value: number; sub?: string }[]; color?: string; format?: (n: number) => string;
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div>
      {rows.map((r, i) => (
        <div className="hbar" key={i} title={r.sub}>
          <span>{r.label}</span>
          <span className="track"><i style={{ width: `${(r.value / max) * 100}%`, background: color }} /></span>
          <span className="num" style={{ textAlign: "right", fontWeight: 600 }}>{format(r.value)}</span>
        </div>
      ))}
    </div>
  );
}

/** Part-to-whole strip (e.g. warm / cooling / cold). Status colours carry a text legend. */
export function Meter({ parts }: { parts: { label: string; value: number; color: string }[] }) {
  const total = parts.reduce((s, p) => s + p.value, 0);
  return (
    <div>
      <div className="meter" role="img" aria-label={parts.map((p) => `${p.label} ${p.value}`).join(", ")}>
        {total === 0 ? <i style={{ flex: 1, background: "var(--sunk)" }} /> :
          parts.filter((p) => p.value > 0).map((p) => <i key={p.label} title={`${p.label}: ${p.value}`} style={{ flex: p.value, background: p.color }} />)}
      </div>
      <div className="meter-legend">
        {parts.map((p) => <span key={p.label}><i style={{ background: p.color }} />{p.label} <b>{p.value}</b></span>)}
      </div>
    </div>
  );
}


/** KPI tile with optional progress bar and comparison delta. */
export function Kpi({ label, value, unit, sub, target, actual, prev, dark, onClick }: {
  label: string; value: ReactNode; unit?: string; sub?: ReactNode;
  target?: number; actual?: number; prev?: number; dark?: boolean; onClick?: () => void;
}) {
  const pct = target && actual != null ? Math.min(1, actual / target) : null;
  return (
    <div className={dark ? "kpi dark" : "kpi"}>
      {onClick ? <button className="k-label" onClick={onClick}>{label}</button> : <span className="k-label">{label}</span>}
      <span className="k-value">{value}{unit && <small>{unit}</small>}</span>
      {(sub || prev != null) && (
        <span className="k-sub">{prev != null && actual != null && <Delta now={actual} prev={prev} />} {sub}</span>
      )}
      {pct != null && <span className="k-bar"><i className={pct >= 1 ? "met" : ""} style={{ width: `${pct * 100}%` }} /></span>}
    </div>
  );
}

export function Delta({ now, prev, suffix = "vs last year" }: { now: number; prev: number; suffix?: string }) {
  if (!prev && !now) return <span className="delta flat">— {suffix}</span>;
  if (!prev) return <span className="delta flat">nothing to compare {suffix.replace(/^vs /, "with ")}</span>;
  const d = (now - prev) / prev;
  const cls = d > 0.005 ? "up" : d < -0.005 ? "down" : "flat";
  return <span className={`delta ${cls}`}>{cls === "up" ? "▲" : cls === "down" ? "▼" : "■"} {Math.abs(Math.round(d * 100))}% {suffix}</span>;
}

/** Values over time on a real date axis. Use dashed for projections (e.g. a proposed illustration). */
export function Lines({ series, height = 260, format = (n: number) => `$${Math.round(n).toLocaleString()}` }: {
  series: { key: string; label: string; color: string; dashed?: boolean; points: { date: string; value: number }[] }[];
  height?: number; format?: (n: number) => string;
}) {
  const t = useTip();
  const W = useWidth(t.ref), H = height, L = 64, R = 14, T = 12, B = 28;
  const all = series.flatMap((s) => s.points);
  if (all.length === 0) return <div className="chart" ref={t.ref}><div className="empty"><b>No values yet</b>Add an actual value or the proposed illustration to see the chart.</div></div>;
  const ts = all.map((p) => new Date(p.date).getTime());
  let t0 = Math.min(...ts), t1 = Math.max(...ts);
  if (t0 === t1) { t0 -= 86400000 * 30; t1 += 86400000 * 30; }
  const max = nice(Math.max(...all.map((p) => p.value)) * 1.05);
  const x = (d: string) => L + ((new Date(d).getTime() - t0) / (t1 - t0)) * (W - L - R);
  const y = (v: number) => T + (H - T - B) * (1 - v / max);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  const years = Math.max(1, Math.round((t1 - t0) / (365 * 86400000)));
  const xticks = Array.from({ length: Math.min(8, years + 1) }, (_, i) => t0 + ((t1 - t0) * i) / Math.min(7, years));
  return (
    <div className="chart" ref={t.ref}>
      <Legend items={series.map((s) => ({ label: s.label, color: s.color, dashed: s.dashed }))} />
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={series.map((s) => s.label).join(" vs ")}>
        <g className="grid">{ticks.map((v) => <line key={v} x1={L} x2={W - R} y1={y(v)} y2={y(v)} />)}</g>
        <g className="axis">
          {ticks.map((v) => <text key={v} x={L - 8} y={y(v) + 4} textAnchor="end">{format(v)}</text>)}
          {xticks.map((tt, i) => <text key={i} x={L + ((tt - t0) / (t1 - t0)) * (W - L - R)} y={H - 8} textAnchor="middle">{new Date(tt).getFullYear()}</text>)}
        </g>
        {series.map((s) => {
          const pts = [...s.points].sort((a, b) => a.date.localeCompare(b.date));
          return (
            <g key={s.key}>
              <path d={pts.map((p, i) => `${i ? "L" : "M"}${x(p.date)},${y(p.value)}`).join(" ")} fill="none" stroke={s.color} strokeWidth={2.5}
                strokeDasharray={s.dashed ? "6 5" : undefined} strokeLinejoin="round" strokeLinecap="round" />
              {pts.map((p) => (
                <circle key={p.date} cx={x(p.date)} cy={y(p.value)} r={4.5} fill="#fff" stroke={s.color} strokeWidth={2} tabIndex={0}
                  onMouseEnter={(e) => t.show(e, { title: new Date(p.date).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }), rows: [{ label: s.label, value: format(p.value), color: s.color }] })}
                  onFocus={(e) => t.show(e, { title: p.date, rows: [{ label: s.label, value: format(p.value), color: s.color }] })}
                  onMouseLeave={t.hide} onBlur={t.hide} />
              ))}
            </g>
          );
        })}
        <line x1={L} x2={W - R} y1={y(0)} y2={y(0)} stroke="#d4d2e2" />
      </svg>
      {t.el}
    </div>
  );
}
